import { type FC } from "react";

import { Card } from "@jti/core-components/components/Card";
import type { ICurrencyEnum } from "@jti/magento";
import { type GetEligibleCampaignsResponseType } from "@jti/rest-api-types";
import { CheckoutV2LoyaltyChoice } from "@jti/store/slices/checkoutV2Slice";
import { useTranslations } from "@jti/ui";
import LoyaltyLogo from "@jti/ui/icons/loyalty_logo.svg";

import { LoyaltyPointsToggle } from "./components/LoyaltyPointsToggle";

import styles from "./LoyaltyPointsView.module.scss";

type LoyaltyPointsViewProps = {
  currency: ICurrencyEnum;
  userPoints: number;
  cartPoints: number;
  loyaltyChoice: CheckoutV2LoyaltyChoice | undefined;
  redemptionTabSymbol: string;
  minVoucherCost: number;
  maxVoucherValue: number;
  onCollect: () => void;
  onRedeem: () => void;
  isRedeemAvailable: boolean;
  vouchers: GetEligibleCampaignsResponseType[];
  isQueryCampaign: boolean;
  isLoyaltyError: boolean;
  loyaltyCampaignId: string;
  onCampaignChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

export const LoyaltyPointsView: FC<LoyaltyPointsViewProps> = ({
  currency,
  userPoints,
  cartPoints,
  loyaltyChoice,
  redemptionTabSymbol,
  minVoucherCost,
  maxVoucherValue,
  onCollect,
  onRedeem,
  isRedeemAvailable,
  vouchers,
  isQueryCampaign,
  isLoyaltyError,
  loyaltyCampaignId,
  onCampaignChange,
}) => {
  const { __ } = useTranslations();

  return (
    <div className={styles.wrapper} data-testid="checkoutLoyaltyPoints">
      <Card variant="level-2">
        <div className={styles.header}>
          <div className={styles.title}>{__("loyalty.pointsBlock.title")}</div>
          <span className={styles.divider} aria-hidden="true" />
          <LoyaltyLogo width={32} height={20} />
        </div>
        <div className={styles.balanceWrapper}>
          <div className={styles.balance}>
            <div className={styles.balanceTitle}>{__("loyalty.pointsBlock.balance")}</div>
            <div className={styles.balanceValue} data-testid="checkoutPoints">
              {userPoints}
            </div>
          </div>
          {loyaltyChoice === CheckoutV2LoyaltyChoice.COLLECT && (
            <div className={styles.upcomingPoints} data-testid="checkoutUpcomingPoints">
              {__("loyalty.pointsBlock.upcomingPoints", [cartPoints])}
            </div>
          )}
        </div>

        <LoyaltyPointsToggle
          minVoucherCost={minVoucherCost}
          maxVoucherValue={maxVoucherValue}
          setCollect={onCollect}
          setRedeem={onRedeem}
          isRedeemAvailable={isRedeemAvailable}
          cartPoints={cartPoints}
          userPoints={userPoints}
          currency={currency}
          redemptionTabSymbol={redemptionTabSymbol}
          loyaltyChoice={loyaltyChoice}
          vouchers={vouchers}
          isQueryCampaign={isQueryCampaign}
          isLoyaltyError={isLoyaltyError}
          loyaltyCampaignId={loyaltyCampaignId}
          handleCampaignChange={onCampaignChange}
        />
      </Card>
    </div>
  );
};
